import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Calendar, ChevronRight, ArrowLeft, Loader2 } from "lucide-react";
import { ArticleSchema, BreadcrumbSchema, setCanonical, setMetaTags } from "@/components/seo/SchemaMarkup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAJINI_URL = "https://storajini.com";

// Simple markdown to HTML + auto-linkify storajini.com
function renderMarkdown(md) {
  if (!md) return "";
  // Auto-linkify plain text "storajini.com" or "Storajini.com" mentions that aren't already links
  let html = md.replace(/(?<!\[)(?<!\()((?:S|s)torajini\.com)(?!\))/g, '[$1](https://storajini.com/search)');
  html = html
    .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold text-gray-900 mt-6 mb-3">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold text-gray-900 mt-8 mb-4">$1</h2>')
    .replace(/^# (.*$)/gim, '<h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-amber-600 hover:text-amber-700 underline font-medium">$1</a>')
    .replace(/^- \[ \] (.*$)/gim, '<li class="flex items-start gap-2 mb-2"><input type="checkbox" disabled class="mt-1" /><span class="text-gray-600">$1</span></li>')
    .replace(/^- \[x\] (.*$)/gim, '<li class="flex items-start gap-2 mb-2"><input type="checkbox" checked disabled class="mt-1" /><span class="text-gray-600">$1</span></li>')
    .replace(/^- (.*$)/gim, '<li class="text-gray-600 mb-1.5 ml-4 list-disc">$1</li>')
    .replace(/^\d+\. (.*$)/gim, '<li class="text-gray-600 mb-1.5 ml-4 list-decimal">$1</li>')
    .replace(/\n\n/g, '</p><p class="text-gray-600 leading-relaxed mb-4">')
    .replace(/\n/g, '<br />');
  html = '<p class="text-gray-600 leading-relaxed mb-4">' + html + '</p>';
  // Clean up empty paragraphs around headings
  html = html.replace(/<p[^>]*>\s*(<h[23][^>]*>)/g, '$1');
  html = html.replace(/(<\/h[23]>)\s*<\/p>/g, '$1');
  html = html.replace(/<p[^>]*>\s*(<li)/g, '<ul class="mb-4">$1');
  html = html.replace(/(<\/li>)\s*<\/p>/g, '$1</ul>');
  return html;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog/${slug}`)
      .then(r => {
        setPost(r.data);
        setCanonical(`/blog/${slug}`);
        setMetaTags({ title: r.data.meta_title || r.data.title, description: r.data.meta_description || r.data.excerpt });
      })
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 text-amber-600 animate-spin" /></div>;
  }

  if (!post) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Post Not Found</h1>
        <Link to="/blog" className="text-amber-600 font-semibold hover:underline">Back to Blog</Link>
      </div>
    );
  }

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Blog", url: "/blog" },
    { name: post.title },
  ];

  return (
    <div data-testid="blog-post-page">
      <ArticleSchema title={post.title} description={post.excerpt} slug={post.slug} datePublished={post.created_at} dateModified={post.updated_at} />
      <BreadcrumbSchema items={breadcrumbs} />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            {breadcrumbs.map((item, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <ChevronRight className="w-3.5 h-3.5" />}
                {item.url ? (
                  <Link to={item.url} className="hover:text-amber-600">{item.name}</Link>
                ) : (
                  <span className="text-gray-900 font-medium truncate max-w-[200px]">{item.name}</span>
                )}
              </span>
            ))}
          </nav>
        </div>
      </div>

      {/* Article */}
      <article className="py-12">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-amber-600 font-medium hover:underline mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-amber-50 text-amber-600 text-xs font-semibold rounded-full capitalize">{post.category}</span>
            <div className="flex items-center gap-1 text-sm text-gray-400">
              <Calendar className="w-4 h-4" />
              {new Date(post.created_at).toLocaleDateString("en-PH", { month: "long", day: "numeric", year: "numeric" })}
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6 leading-tight">{post.title}</h1>

          <div className="article-intro text-lg text-gray-500 mb-8 leading-relaxed">{post.excerpt}</div>

          <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }} />

          {/* CTA */}
          <div className="mt-12 p-8 bg-amber-50 rounded-2xl border border-amber-100 text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Need Storage or Parking?</h3>
            <p className="text-gray-500 mb-4">Find verified spaces near you on Storajini.com</p>
            <a
              href={`${STORAJINI_URL}/search`}
             
             
              data-testid="blog-cta-btn"
              className="inline-flex items-center px-6 py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors"
            >
              Browse Spaces
            </a>
          </div>

          {/* Explore Related - Internal Linking (v3.9 requirement) */}
          <div className="mt-10 p-6 bg-gray-50 rounded-2xl border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Explore Related</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Link to="/storage/manila" className="text-sm text-amber-600 hover:underline">Storage in Manila</Link>
              <Link to="/parking/makati" className="text-sm text-amber-600 hover:underline">Parking in Makati</Link>
              <Link to="/moving/cebu-city" className="text-sm text-amber-600 hover:underline">Moving to Cebu</Link>
              <Link to="/storage/davao-city" className="text-sm text-amber-600 hover:underline">Storage in Davao</Link>
              <Link to="/compare" className="text-sm text-amber-600 hover:underline">Compare Cities</Link>
              <Link to="/parking/taguig" className="text-sm text-amber-600 hover:underline">Parking in BGC</Link>
              <Link to="/storage/quezon-city" className="text-sm text-amber-600 hover:underline">Storage in QC</Link>
              <Link to="/all-cities" className="text-sm text-amber-600 hover:underline">All Cities Guide</Link>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
